import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { SAVE_GENERIC_FILTER_TEMPLATE_DETAILS } from '../mutations';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { Card } from '@totalsoft/rocket-ui';
import { GENERIC_FILTER_TEMPLATE_DETAILS_SAVED } from '../subscriptions';
import { GENERIC_FILTER_TEMPLATE_DETAILS_QUERY } from '../queries';
import { useToast } from 'hooks/toasts/useToast';
import GenericFilterComposer, { defaultTemplate, setInitialData } from '@ts-private/generic-filter-composer';
import useGenericFilterData from 'features/common/hooks/useGenericFilterData';
import WrappedFakeText from 'components/common/WrappedFakeText';
import FlexBox from 'components/common/FlexBox';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_GENERIC_FILTER_TEMPLATES_WRITE } from 'constants/rightsCodes';

const actionsSlotId = 'template-details-card-actions-slot';

const GenericFilterTemplateDetailsComponent = ({ id }) => {
  const addToast = useToast();
  const { t } = useTranslation();
  const canWrite = usePermission(ADMIN_GENERIC_FILTER_TEMPLATES_WRITE);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(defaultTemplate);

  // #region Data fetching

  const { loading } = useQuery(GENERIC_FILTER_TEMPLATE_DETAILS_QUERY, {
    variables: { id },
    skip: !id,
    onCompleted: data => {
      const templateData = {
        withTemplate: false,
        templateId: null,
        template: {
          phrase: data.genericFilterTemplateDetails.phrase || null,
          conditions: data.genericFilterTemplateDetails.conditions || []
        }
      };
      resetLens(templateData);
      setInitialData(templateData);
    }
  });

  const { parameters, operators, loadValues, loadPagedValues } = useGenericFilterData();

  // #endregion

  // #region Side effects

  useEffect(() => {
    resetLens(prev => ({ ...prev, disabled: !canWrite }));
  }, [canWrite, resetLens]);

  // #endregion

  // #region Save

  const [saveDetails] = useMutation(SAVE_GENERIC_FILTER_TEMPLATE_DETAILS);

  const handleSave = data => {
    (lens.disabled |> set)(true);
    saveDetails({ variables: { input: { id, ...data.template } } });
  };

  useSubscription(GENERIC_FILTER_TEMPLATE_DETAILS_SAVED, {
    onData: ({ client }) => {
      const currentData = lens |> get;
      const newData = {
        ...currentData,
        disabled: false,
        template: { ...currentData.template, conditions: currentData.template.conditions.map(x => ({ ...x, editing: false })) }
      };
      resetLens(newData);
      setInitialData(newData);
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'genericFilterTemplateDetails' });
      addToast(t('GenericFilterTemplate.Saved'), 'success');
    }
  });

  // #endregion

  return (
    <Card
      icon={ViewQuiltIcon}
      iconColor='primary'
      title={t('EntityFilter.Filters')}
      actions={<FlexBox gap={0.5} alignItems='center' id={actionsSlotId}></FlexBox>}
    >
      {loading && <WrappedFakeText lines={5} />}
      {!loading && (
        <GenericFilterComposer
          lens={lens}
          dirtyInfo={dirtyInfo}
          resetLens={resetLens}
          parameters={parameters}
          operators={operators}
          loadValues={loadValues}
          loadPagedValues={loadPagedValues}
          onSave={handleSave}
          withTemplateGeneralData
          conditionsViewMode='table'
          actionsSlotId={actionsSlotId}
        />
      )}
    </Card>
  );
};

GenericFilterTemplateDetailsComponent.propTypes = {
  id: PropTypes.number
};

export default GenericFilterTemplateDetailsComponent;
